/**
 * The parts of a click this module judges.
 *
 * Structural rather than React's `MouseEvent`, because `lib/` holds pure
 * helpers and imports no React. A React synthetic event and a DOM event both
 * satisfy this shape, so both call sites pass one straight in.
 */
export interface ClickLike {
  /** 0 is the primary button; 1 is the middle button. */
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

/**
 * Whether a click is a plain activation the app should answer itself.
 *
 * A held modifier or the middle button means the reader is asking the BROWSER
 * for something else — a new tab, a new window — and the in-app handler must
 * not answer instead. Every caller reads this the same way: true means
 * `preventDefault` and route in place, false means let the event alone and
 * let the `href` do its job.
 *
 * The middle button is belt and braces. A modern engine fires `auxclick` for
 * it and never `click`, so a React `onClick` does not see one; this keeps the
 * answer right if a stray `click` carries a non-primary button anyway.
 *
 * @param event - The click.
 * @returns True when no modifier was held and the primary button was used.
 */
export function isPlainClick(event: ClickLike): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}
